'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useClickOutside, useNoHover } from '@overlapdev/hooks';
import Link from 'next/link';
import Image from 'next/image';
import parse from 'html-react-parser';
import { MenuItemType, PageType } from '@overlapdev/types';
import { ArrowDownSVG, CircleArrowSVG } from 'components/svg';
import MegaNavItem from './item';

const MegaNav = ({ item }: { item: MenuItemType }) => {
	//__Ref
	const ref = useRef(null);

	//__Hooks
	const pathname = usePathname();
	const noHover = useNoHover();
	const clickOutside = useClickOutside(ref);

	//__Variables
	const { acf, child_items, classes, id, link, target, title } = item || {};
	const { add_blog_posts, blog_posts } = acf || {};
	const no_link = classes?.includes('label') || false;

	const current_cat_path = `/${pathname.split('/')[1]}`;

	//__State
	const [currentId, setCurrentId] = useState(0);
	const [open, setOpen] = useState(false);
	const [fade, setFade] = useState(false);
	const [offset, setOffset] = useState<number | string>(0);
	const [timeoutHandler, setTimeoutHandler] = useState<ReturnType<typeof setTimeout> | null>(null);
	const [hoverTimeout, setHoverTimeout] = useState(true);

	//__Function
	const openMega = () => {
		//Timeout to prevent accidental hovers
		setTimeoutHandler(
			setTimeout(() => {
				document.body.classList.add('mega_open');
				if (classes.includes('primary')) {
					document.body.classList.add('primary_mega');
				} else if (classes.includes('secondary')) {
					document.body.classList.add('secondary_mega');
				} else {
					document.body.classList.add('neutral_mega');
				}
				setOpen(true);
				setFade(true);
			}, 200)
		);
	};
	const closeMega = () => {
		document.body.classList.remove('mega_open', 'primary_mega', 'secondary_mega', 'neutral_mega');
		setFade(false);
		setTimeout(() => {
			setOpen(false);
			setOffset(0);
			setCurrentId(0);
		}, 300);
		if (timeoutHandler) {
			clearTimeout(timeoutHandler);
		}
	};

	const handleMouseEnter = () => {
		if (!hoverTimeout && !noHover) {
			openMega();
		}
	};

	const handleMouseLeave = () => {
		if (!noHover) {
			closeMega();
		}
	};

	//For devices with no Hover
	const handleClick = (e: any) => {
		e.preventDefault();
		if (noHover) {
			if (open) {
				closeMega();
			} else {
				openMega();
			}
		}
	};

	//__Use Effect
	useEffect(() => {
		setTimeout(() => {
			setHoverTimeout(false);
		}, 300);
	}, []);

	useEffect(() => {
		closeMega();
	}, [pathname]);

	useEffect(() => {
		if (clickOutside) {
			closeMega();
		}
	}, [clickOutside]);

	//__Callbacks
	const handleRoot = useCallback((offset: number | string, current_id: number) => {
		setOffset(offset);
		setCurrentId(current_id);
	}, []);

	return (
		<li key={`mega_nav_item-${id}`} className={`mega_nav_item ${classes.toString().replace(/,/g, ' ')}${pathname === link || current_cat_path === link ? ' current' : ''}`} id={id?.toString() || ''} onMouseEnter={() => handleMouseEnter()} onMouseLeave={() => handleMouseLeave()} ref={ref}>
			{no_link ? (
				<span className='flex gap-3' onClick={(e) => handleClick(e)}>
					<span>{parse(title || '')}</span>
					<ArrowDownSVG className='arrow' width={10} />
				</span>
			) : (
				<Link className='flex gap-3 items-center' href={link || ''} as={link} passHref={target === '_blank' ? true : false} target={target}>
					<span>{parse(title || '')}</span>
					<span onClick={(e) => handleClick(e)}>
						<ArrowDownSVG className='arrow' width={10} />
					</span>
				</Link>
			)}

			<div className={`absolute h-10 left-0 bottom-0 w-full ${open ? 'block' : 'hidden'}`} />
			<div className={`mega_wrapper absolute duration-300 ease-out gutter-x h-[calc(95vh-142px)] overflow-hidden rounded-b-3xl text-black transition-opacity top-[94px] z-0 ${fade ? 'opacity-100' : 'opacity-0'} ${open ? 'left-0 w-full' : '-left-full w-0'}`}>
				{add_blog_posts && blog_posts?.length ? <div className='mega_blog_bg' /> : null}
				<div className='flex inner h-full relative'>
					<div className={`mega_menu overflow-auto overflow-x-hidden pt-8 xl:pt-16 ${add_blog_posts && blog_posts?.length ? 'w-1/2' : 'w-full'}`}>
						<div className='h-full relative transition-ease' style={{ left: offset }}>
							<ul>
								{child_items.map((child_item) => (
									<MegaNavItem key={`mega_menu-${child_item.id}`} item={child_item} level={0} main_title={item.title} parent={item} handleRoot={handleRoot} current_id={currentId} root_id={parseInt(child_item.id.toString(), 10)} />
								))}
							</ul>
						</div>
					</div>
					{add_blog_posts && blog_posts?.length ? (
						<div className='overflow-auto pl-8 py-8 shrink-0 w-1/2 xl:pl-16 xl:py-16'>
							<span className='block mb-5 text-white'>Blogs</span>
							<div className='flex flex-col gap-5'>
								{blog_posts.map((post: PageType) => {
									const { acf, id, relative_path, post_title } = post || {};
									const { excerpt, grid_image } = acf || {};

									return (
										<Link key={`mega_blog_post_${id}`} href={relative_path} className='bg-white flex group h-43 overflow-hidden rounded-2xl transition-ease'>
											{grid_image?.url ? (
												<div className='overflow-hidden relative shrink-0 w-50'>
													<Image src={grid_image.url} alt={grid_image.alt} fill className='object-cover transition-ease group-hover:scale-110' />
												</div>
											) : null}
											<div className='flex flex-col px-4 py-3 xl:px-8 xl:py-6'>
												<div className='grow'>
													<span className='block text-lg'>{parse(post_title.length > 30 ? `${post_title.substring(0, 30)}...` : post_title)}</span>
													{excerpt ? <span className='block font-medium mt-2'>{parse(excerpt.length > 85 ? `${excerpt.substring(0, 80)}...` : excerpt)}</span> : null}
												</div>
												<div className='flex gap-2 items-center mt-4 text-dark-grey'>
													<span>Read More</span>
													<CircleArrowSVG width={18} />
												</div>
											</div>
										</Link>
									);
								})}
							</div>
						</div>
					) : null}
				</div>
			</div>
		</li>
	);
};

export default MegaNav;
